import useResizeObserver from "@react-hook/resize-observer";
import { useEffect, useRef } from "react";

import { Typography } from "antd";
import {
  DEFAULT_FONT_SIZE,
  THEME_FONT_FAMILY,
  resolveFontFamily
} from "constants/style.constants";
import styled from "styled-components";

import { IChartScreenshotSettings } from "models/chart";
import { IScreenshotAbbreviation } from "models/screenshot";

import { applyAbbreviations } from "components/screenshot/utils";
import { useChartState } from "components/shared/charts";

import { useChartStatsQuery } from "./hooks";

export interface StatsModel {
  wellList: string[];
  setting: IChartScreenshotSettings;
  abbreviations: IScreenshotAbbreviation[];
}
export default function Stats({ wellList, setting, abbreviations }: StatsModel) {
  const chartRef = useRef(null);

  const containerRef = useRef(null);
  const { data, updateData } = useChartStatsQuery(wellList);
  const { screenshot } = useChartState();

  const applyScreenshotAbbreviations = setting?.applyAbbreviations && screenshot?.visible;

  useEffect(() => {
    updateData();
  }, [updateData, wellList]);

  useResizeObserver(containerRef.current, () => {
    if (!chartRef.current) {
      return;
    }
    chartRef.current.resize();
  });
  if (!data) {
    return null;
  }
  return (
    <Wrapper
      ref={containerRef}
      fontFamily={resolveFontFamily(
        screenshot?.visible ? setting?.fontFamily : THEME_FONT_FAMILY
      )}
      fontSize={screenshot?.visible ? setting?.titleFontSize : DEFAULT_FONT_SIZE}>
      <Typography.Text className="title">
        {data.lastQuarterTitle}{" "}
        {applyScreenshotAbbreviations
          ? applyAbbreviations("Production", abbreviations)
          : "Production"}
      </Typography.Text>
      <Typography.Text className="value" strong>
        {Math.round(data.lastQuarterBitumen)}{" "}
        {applyScreenshotAbbreviations
          ? applyAbbreviations("bbl/d", abbreviations)
          : "bbl/d"}
      </Typography.Text>

      <Typography.Text className="title">{data.lastQuarterTitle} iSOR</Typography.Text>
      <Typography.Text className="value" strong>
        {data.lastQuarterISor}{" "}
        {applyScreenshotAbbreviations
          ? applyAbbreviations("bbl/bbl", abbreviations)
          : "bbl/bbl"}
      </Typography.Text>

      <Typography.Text className="title"> Current cSOR</Typography.Text>
      <Typography.Text className="value" strong>
        {data.currentCSor}{" "}
        {applyScreenshotAbbreviations
          ? applyAbbreviations("bbl/bbl", abbreviations)
          : "bbl/bbl"}
      </Typography.Text>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  font-family: ${(s) => s.fontFamily};
  font-size: ${(s) => s.fontSize}px;
  padding: 5px;
  display: flex;
  flex-direction: row;
  .title {
    padding-left: 15px;
  }
  .value {
    padding-left: 5px;
  }
`;
